import type {
  Alert,
  Classification,
  Entity,
  EntityMention,
  IngestedItem,
  ItemImpact,
  Module,
  RealityScore,
  Source,
} from "@bios/types";

const PRODUCTION_API_BASE = "https://bios-api.ccwfashions.workers.dev";
const LOCAL_API_BASE = "http://localhost:8787";

const BASE = resolveApiBaseUrl();

function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  if (configured) return configured.replace(/\/+$/, "");

  if (import.meta.env.DEV && typeof window !== "undefined" && isLocalHost(window.location.hostname)) {
    return LOCAL_API_BASE;
  }

  return PRODUCTION_API_BASE;
}

function isLocalHost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

export interface DashboardResponse {
  module: Module;
  total_items: number;
  signal_items: number;
  noise_items: number;
  classified_items: number;
  avg_reality_score: number;
  top_topics: { topic: string; count: number }[];
  recent_alerts: Alert[];
  signal_noise_series: { date: string; signal: number; noise: number }[];
  content_type_breakdown: { content_type: string; count: number }[];
}

export interface ItemDetailResponse {
  item: IngestedItem;
  classification: Classification | null;
  reality_score: RealityScore | null;
  item_impact: ItemImpact | null;
}

type Fetcher = (path: string, init?: RequestInit) => Promise<Response>;

export class BiosApi {
  constructor(private getToken: () => Promise<string | null>) {}

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = await this.getToken();
    const res = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(init?.body ? { "content-type": "application/json" } : {}),
      },
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => "");
      throw new Error(`api_${res.status}: ${detail}`);
    }
    return (await res.json()) as T;
  }

  me() {
    return this.request<{ user: { userId: string; role: "admin" | "analyst" | "viewer"; email?: string } }>(
      `/v1/me`,
    );
  }
  dashboard(module: Module) {
    return this.request<DashboardResponse>(`/v1/dashboard?module=${module}`);
  }
  sources(module?: Module) {
    return this.request<{ sources: Source[] }>(`/v1/sources${module ? `?module=${module}` : ""}`);
  }
  createSource(body: Partial<Source>) {
    return this.request<{ source: Source }>(`/v1/sources`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }
  updateSource(id: string, body: Partial<Source>) {
    return this.request<{ source: Source }>(`/v1/sources/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }
  deleteSource(id: string) {
    return this.request<{ ok: true }>(`/v1/sources/${id}`, { method: "DELETE" });
  }
  triggerSource(id: string) {
    return this.request<{ ok: true }>(`/v1/sources/${id}/trigger`, { method: "POST" });
  }
  items(module: Module, limit = 50) {
    return this.request<{ items: IngestedItem[]; next_cursor: string | null }>(
      `/v1/items?module=${module}&limit=${limit}`,
    );
  }
  item(id: string) {
    return this.request<ItemDetailResponse>(`/v1/items/${id}`);
  }
  entities(module: Module) {
    return this.request<{ entities: Entity[] }>(`/v1/entities?module=${module}`);
  }
  entity(id: string) {
    return this.request<{ entity: Entity; mentions: EntityMention[] }>(`/v1/entities/${id}`);
  }
  alerts(module: Module) {
    return this.request<{ alerts: Alert[] }>(`/v1/alerts?module=${module}`);
  }
  overview(
    timeWindow = "7d",
    opts: {
      includeGeneral?: boolean;
      geoScope?: string;
      geoState?: string;
      geoCity?: string;
      scope?: string;
      state?: string;
      city?: string;
    } = {},
  ) {
    const q = new URLSearchParams({ time_window: timeWindow });
    if (opts.includeGeneral) q.set("include_general", "1");
    const geoScope = opts.geoScope ?? opts.scope;
    const geoState = opts.geoState ?? opts.state;
    const geoCity = opts.geoCity ?? opts.city;
    if (geoScope && geoScope !== "national") q.set("geo_scope", geoScope);
    if (geoState) q.set("geo_state", geoState);
    if (geoCity) q.set("geo_city", geoCity);
    return this.request<OverviewResponse>(`/v1/overview?${q.toString()}`);
  }
  intelligence(payload: {
    question: string;
    modules?: string[];
    time_window?: string;
    mode?: IntelligenceMode;
    // Phase 21
    geo_scope?: string;
    geo_state?: string;
    geo_city?: string;
  }) {
    return this.request<IntelligenceResponse>(`/v1/intelligence/query`, {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }
}

export interface OverviewModuleView {
  module: string;
  total_items: number;
  reality_pct: number;
  real_pct: number;
  narrative_pct: number;
  reality_delta: number;
  trend: "up" | "down" | "flat";
  status: "noise-heavy" | "mixed" | "signal-emerging" | "quiet";
}

export interface OverviewInsight {
  id: string;
  module: string;
  title: string;
  what_happened: string;
  why_matters: string;
  reality_score: number | null;
  signal_type: string | null;
  url: string | null;
  ingested_at: string;
  cross_module_priority_score?: number;
  cluster_id?: string | null;
  convergence_count?: number;
  geo_convergence_count?: number;
}

export type ConvergenceType =
  | "strategic"
  | "narrative"
  | "procedural"
  | "symbolic";

export interface SignalCluster {
  cluster_id: string;
  geo: {
    city: string | null;
    state: string | null;
  };
  entities: string[];
  modules: string[];
  related_gaps: string[];
  combined_priority_score: number;
  convergence_count: number;
  geo_convergence_count: number;
  event_count: number;
  headline: string;
  // Phase 37 — present on all clusters returned by the API after this
  // version. Older payloads may not include them; consumers default to
  // "strategic" / 0.5 / 0 to avoid breaking.
  convergence_type?: ConvergenceType;
  strategic_relevance_score?: number;
  shared_dimensions?: number;
}

export interface OverviewTrendPoint {
  date: string;
  real: number;
  narrative: number;
}

export interface FocusRecommendation {
  label: string;
  reason: string;
  module?: string;
  gap_kind?: string;
  query: string;
}

export interface ModuleImpactSlice {
  module: string;
  total_items: number;
  black_relevant_items: number;
  measurable_items: number;
  measurable_pct: number;
  by_population: Record<string, number>;
  by_state: Record<string, number>;
}

export interface SystemImpactKpi {
  total_items: number;
  black_signals_pct: number;
  policy_black_impact_pct: number;
  capital_black_pct: number;
  outcome_gaps: number;
  modules: ModuleImpactSlice[];
  black_relevant_items: number;
  measurable_black_items: number;
  capital_destination: Record<string, number>;
  education_student_population: Record<string, number>;
}

export interface SystemInterpretation {
  headline: string;
  subline: string;
  statements: string[];
}

export interface NearImpactItem {
  id: string;
  module: string;
  title: string;
  url: string | null;
  population_impact: string | null;
  impact_state: string | null;
  impact_proximity: string | null;
  ingested_at: string;
}

export interface CoverageInfo {
  total_items: number;
  classified_items: number;
  unknown_population_items: number;
  unknown_pct: number;
  is_incomplete: boolean;
}

export type GapSeverity = "critical" | "high" | "medium" | "low";

export interface GapDetail {
  kind: string;
  count: number;
  modules: string[];
  what: string;
  why: string;
  action: string;
  query: string;
  severity: GapSeverity;
  priority_score: number;
}

// Phase 20.4
export interface SystemLink {
  statement: string;
  modules: string[];
  severity: GapSeverity;
  gap_kinds: string[];
}
export interface SystemCausality {
  system_links: SystemLink[];
  root_failure_mode: string;
}

export type AccountabilityRole =
  | "originator"
  | "amplifier"
  | "funder"
  | "policy_actor"
  | "institution";
export interface AccountabilityActor {
  name: string;
  module: string;
  role: AccountabilityRole;
  involvement: string;
  severity: GapSeverity;
}
export interface Accountability {
  actors: AccountabilityActor[];
  summary: string;
}

export interface ForwardProjection {
  consequences: string[];
}

export interface GapSummary {
  total_black_impact_gaps: number;
  by_kind: GapDetail[];
}

export interface RootCause {
  cause: string;
  modules: string[];
  gap_kind?: string;
}

export type UrgencyLevel = "critical" | "elevated" | "low";
export interface Urgency {
  level: UrgencyLevel;
  banner: string;
  reasons: string[];
}

export interface OverviewResponse {
  time_window: string;
  days: number;
  generated_at?: string;
  cached?: boolean;
  global_metrics: {
    avg_reality: number;
    real_pct: number;
    narrative_pct: number;
    total_signals: number;
    total_items: number;
  };
  modules: OverviewModuleView[];
  top_insights: OverviewInsight[];
  signal_clusters?: SignalCluster[];
  strongest_active_cluster?: SignalCluster | null;
  strongest_geo_concentration?: SignalCluster | null;
  strongest_accountability_failure?: GapDetail | null;
  trend_series: OverviewTrendPoint[];
  focus_recommendations: FocusRecommendation[];
  system_impact: SystemImpactKpi;
  // Phase 20.1
  interpretation?: SystemInterpretation;
  near_impact?: NearImpactItem[];
  coverage?: CoverageInfo;
  gap_summary?: GapSummary;
  // Phase 20.2
  root_causes?: RootCause[];
  urgency?: Urgency;
  // Phase 20.2-fix
  headline_truth?: string | null;
  // Phase 20.3
  scope_of_failure?: string | null;
  // Phase 20.4
  system_causality?: SystemCausality;
  accountability?: Accountability;
  forward_projection?: ForwardProjection;
  // Phase 21
  geo_filter?: { scope?: string; state?: string | null; city?: string | null };
  impact_by_location?: ImpactByLocation;
  low_data_warning?: LowDataWarning | null;
}

export interface LocationStat {
  state: string;
  total_items: number;
  black_relevant_items: number;
  measurable_black_items: number;
  measurable_pct: number;
}
export interface CityStat {
  city: string;
  state: string | null;
  total_items: number;
  black_relevant_items: number;
}
export interface ImpactByLocation {
  top_states_by_impact: LocationStat[];
  zero_impact_states: LocationStat[];
  top_cities_by_signal: CityStat[];
}

export interface LowDataWarning {
  selected_scope: string;
  selected_label: string;
  total_items: number;
  threshold: number;
}

export type IntelligenceMode = "brief" | "priorities" | "gaps" | "track" | "shifts";

export interface IntelligenceEvidence {
  type: "item" | "alert" | "entity";
  id: string;
  module?: string;
  title: string;
  url?: string;
  detail?: string;
  reality_score?: number;
  signal_type?: string;
}

export interface IntelligenceRelatedEntity {
  id: string;
  name: string;
  type: string;
  modules: string[];
  mention_count: number;
}

export type DecisionType =
  | "OPPORTUNITY"
  | "RISK"
  | "MISDIRECTION"
  | "EMERGING_TREND"
  | "WATCHLIST";

export interface ActNowItem {
  title: string;
  entity: string;
  modules: string[];
  reason: string;
  recommended_action: string;
  confidence: number;
  decision_kind: DecisionType;
}

export interface DecisionAction {
  action: string;
  target_entity: string;
  modules: string[];
  reason: string;
  urgency: "HIGH" | "MEDIUM" | "LOW";
  evidence_ids: string[];
}

export interface Originator {
  name: string;
  module: string;
  tier: string;
  weight: number;
  reason: string;
}
export interface Amplifier {
  name: string;
  module: string;
  item_count: number;
  avg_reality: number;
  weight: number;
  reason: string;
}
export interface PowerActor {
  name: string;
  type: string;
  subtype?: string | null;
  modules: string[];
  mention_count: number;
  reason: string;
}
export interface InfluenceMap {
  summary: string;
  originators: Originator[];
  amplifiers: Amplifier[];
  power_actors: PowerActor[];
}

export interface Confidence {
  score: number;
  drivers: string[];
  limitations: string[];
}

export type GapKind =
  | "narrative_inflation"
  | "silent_capital"
  | "outcome_void"
  | "entity_isolation"
  | "media_without_capital"
  | "high_narrative"
  | "policy_without_capital"
  | "capital_without_policy"
  | "policy_without_outcome"
  | "performative_policy"
  | "aligned_policy"
  | "black_impact_gap"
  | "funding_not_reaching_black_communities"
  | "narrative_vs_reality_black"
  | "policy_without_black_outcome"
  | "education_outcome_gap_black"
  | "workforce_without_outcome"
  | "training_without_placement"
  | "hiring_without_retention"
  | "wage_gap_persistence"
  | "layoffs_without_recovery"
  | "funding_without_jobs"
  | "enforcement_without_outcome"
  | "legal_action_without_reform"
  | "rights_restriction_gap"
  | "policing_without_accountability"
  | "settlement_without_change"
  | "convergence_without_outcome"
  | "multi_module_failure"
  | "repeated_policy_without_result"
  | "recurring_legal_without_reform"
  | "regional_impact_gap"
  | "funding_not_reaching_black_communities_by_state"
  | "policy_without_local_outcome";

export interface GapFinding {
  kind: GapKind;
  label: string;
  detail: string;
  entity_id?: string;
  item_id?: string;
  module?: string;
  modules?: string[];
  metric?: number;
}

export interface RankedSignal {
  rank: "critical" | "supporting";
  source_kind: "entity" | "alert" | "gap" | "item";
  source_id: string;
  title: string;
  detail?: string;
  modules: string[];
  reality_score?: number;
  cross_module_score: number;
  reality_component: number;
  recency_component: number;
  rank_score: number;
  rationale: string;
}

export interface IntelligenceResponse {
  question: string;
  mode: IntelligenceMode;
  modules: string[];
  time_window: string;
  // Phase 11.2 contract
  top_priority: string;
  decision_type: DecisionType;
  whats_real: string;
  who_is_shaping_it: InfluenceMap;
  so_what: string;
  what_to_do: DecisionAction[];
  do_not_do: DecisionAction[];
  act_now: ActNowItem[];
  mistake: string;
  confidence: Confidence;
  evidence: IntelligenceEvidence[];
  // Backward-compat + retrieval
  answer: string;
  critical_signals: RankedSignal[];
  supporting_signals: RankedSignal[];
  key_insights: string[];
  where_impact_is_closest: Array<{
    id: string;
    module: string;
    title: string;
    url: string | null;
    impact_state: string | null;
    impact_proximity: string | null;
    reason: string;
  }>;
  related_entities: IntelligenceRelatedEntity[];
  related_modules: string[];
  gaps: GapFinding[];
  llm_used: boolean;
}
